import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const CleanVariant = ({ heroContent, brand }) => {
  const { cta, domainConfig } = useTheme();
  const heroRef = useRef(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrolled = window.pageYOffset;
        const parallax = scrolled * 0.3;
        heroRef.current.style.transform = `translateY(${parallax}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-white">
      {/* Minimal Background Pattern */}
      <div className="absolute inset-0 opacity-3">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.02'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column - Content */}
          <div className="space-y-10">
            {/* Main Heading */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight tracking-tight">
                {h1Title || heroContent.title}
              </h1>
              <div className="w-24 h-1 bg-gray-300"></div>
              <p className="text-xl text-gray-500 leading-relaxed max-w-2xl font-light">
                {heroContent.subtitle}
              </p>
            </div>

            {/* Features List - Minimal */}
            <div className="space-y-4">
              {heroContent.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-600 font-light text-lg">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons - Minimal */}
            <div className="flex flex-col sm:flex-row gap-6 pt-8">
              {renderCtaButton(cta?.primary, 'Request Demo', 'inline-flex items-center justify-center px-10 py-4 bg-gray-900 text-white font-light text-lg rounded-none hover:bg-gray-800 transition-all duration-300 border-2 border-gray-900 hover:border-gray-800', domainConfig?.shopLink)}
              {renderCtaButton(cta?.secondary, 'Learn More', 'inline-flex items-center justify-center px-10 py-4 border-2 border-gray-300 text-gray-600 font-light text-lg rounded-none hover:border-gray-400 hover:text-gray-800 transition-all duration-300', domainConfig?.shopLink)}
            </div>

            {/* Minimal Stats */}
            <div className="pt-12 border-t border-gray-100">
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-light text-gray-900">50,000+</div>
                  <div className="text-sm text-gray-500 font-light">Global Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-gray-900">4.7 million</div>
                  <div className="text-sm text-gray-500 font-light">Vehicle Subscriptions</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-gray-900">25+ years</div>
                  <div className="text-sm text-gray-500 font-light">Innovation</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Images */}
          <div className="relative">
            {/* Main Image - Clean Frame */}
            <div className="relative">
              <div className="absolute inset-0 bg-gray-100 transform rotate-3"></div>
              <img
                src={heroContent.mainImage}
                alt={heroContent.title}
                className="relative w-full h-auto shadow-lg"
                onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
              />
            </div>

            {/* Secondary Image - Floating */}
            {heroContent.secondaryImage && (
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-white shadow-lg border border-gray-100">
                <img
                  src={heroContent.secondaryImage}
                  alt="Secondary"
                  className="w-full h-full object-cover"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                />
              </div>
            )}

            {/* Minimal Decorative Elements */}
            <div className="absolute -bottom-4 -left-4 w-2 h-32 bg-gray-200"></div>
            <div className="absolute -top-4 -left-4 w-32 h-2 bg-gray-200"></div>
          </div>
        </div>
      </div>

      {/* Bottom Line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200"></div>
    </section>
  );
};

export default CleanVariant;
